import { Tooltip } from "@mui/material";
import Button from "components/common/Button";
import CircularProgressBar from "components/common/CircularProgress";
import Container from "components/common/CustomContainer";
import NoDataBanner from "components/common/NoDataBanner";
import Typography from "components/common/Typography";
import { colorProps } from "components/common/UserAvatarList/colorProps";
import AvatarItem from "components/common/UserAvatarList/item";
import { variantProp } from "components/common/UserAvatarList/variantProp";
import VerticalTimeline from "components/common/VerticleTimeline";
import {
  fetchTeamMemberDetailsAsync,
  fetchTeamMemberProgressAsync
} from "components/features/dashboard/HotelUserDashboard.slice";
import { getHotelUserDashboardInncode } from "components/features/dashboard/HotelUserDashboardHelper";
import { uniqueId } from "lodash";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { getUIFormatDate } from "utils/DateHelper";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import "./TeamProgressTracker.scss";

export default function TeamProgressTracker() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const innCode = getHotelUserDashboardInncode();
  const isLeadership = useAppSelector((state) => state.app.loggedInUserDetails.isLeadership);

  const {
    teamProgress,
    overdueActionsCount,
    activeActionsCount,
    completedActionsCount,
    totalActionsCount,
    teamMemberDetails
  } = useAppSelector((state) => state.hotelUserDashboardSlice.teamProgressDetails);

  const infoContent = [
    {
      data: {
        completed: completedActionsCount,
        overdue: 0,
        total: totalActionsCount
      },
      percent: (completedActionsCount / totalActionsCount) * 100,
      label: "Team Progress",
      tooltip: "Percentage of Completed Actions"
    },
    {
      data: {
        completed: 0,
        overdue: overdueActionsCount,
        total: activeActionsCount
      },
      percent: (overdueActionsCount / activeActionsCount) * 100,
      label: "Overdue Actions"
    }
  ];

  const fetchTeamProgressDetails = async () => {
    const response = await dispatch(fetchTeamMemberProgressAsync({ inncodes: innCode }));
    if (response?.payload?.teamMembers?.length)
      dispatch(
        fetchTeamMemberDetailsAsync({
          hltIds: response.payload.teamMembers
        })
      );
  };

  useEffect(() => {
    if (innCode) fetchTeamProgressDetails();
  }, [innCode]);

  return (
    <>
      <Container className="team_progress">
        <Container className="team_progress-tracker" scroll="content">
          <div className="header">
            <Typography variant={"h5"} showBold textAlign={"left"}>
              Team Progress
            </Typography>
            {isLeadership && (
              <Button
                size="Small"
                label="See All Members"
                variant="secondary"
                onClick={() => {
                  navigate("/?parentTab=Property+Overview&childTab=Members");
                }}
              />
            )}
          </div>

          {teamProgress?.length ? (
            <VerticalTimeline
              items={
                teamProgress?.map((element: any) => {
                  const currUser = teamMemberDetails?.find((u: any) => {
                    return u.hltId === element.assignee;
                  });

                  return {
                    id: uniqueId("team-tracker-"),
                    image: (
                      <AvatarItem
                        sizeProperty={variantProp.regular}
                        colorProperty={colorProps.dashboard}
                        user={currUser || { firstName: "Unidentified", lastName: "User" }}
                      />
                    ),
                    onclick: () => {
                      navigate(
                        `/myplandetails/${element?.planId}/${element?.sectionId}/${element?.cardId}?openCard=true`
                      );
                    },
                    content: (
                      <>
                        <Typography variant={"label"} color={tokens.Text.General.Tertiary}>
                          {getUIFormatDate(element.latestActivityDate)}
                        </Typography>
                        <Typography variant={"body2"}>
                          {currUser
                            ? getUserNameWithConditionalCheck(
                                currUser?.displayName,
                                currUser?.firstName,
                                currUser?.lastName
                              )
                            : "Unidentified User"}
                        </Typography>
                        <Typography variant={"body2"} showBold textAlign={"left"}>
                          {element.cardTitle}
                        </Typography>
                        <Typography
                          component={"span"}
                          variant={"label"}
                          showBold
                          className={`action-details ${element.isOverdue ? "Overdue" : element.status}`}
                        >
                          {element.isOverdue ? "Overdue" : element.status}
                        </Typography>
                      </>
                    )
                  };
                }) || []
              }
            />
          ) : (
            <NoDataBanner />
          )}
        </Container>
        <div className="team_progress-info">
          {infoContent.map((item: any) => {
            return (
              <Container className="team_progress-info-item">
                <CircularProgressBar size={40} strokeWidth={6} data={item.data} />
                <Tooltip title={item.tooltip} placement={item.tooltipDirection} arrow>
                  <div className="team_progress-info-item-content">
                    <Typography variant="h4" showBold color={tokens.Text.General.Tertiary}>
                      {Math.round(item.percent) || 0}
                      <Typography component={"span"} variant="body2" showBold color={tokens.Text.General.Tertiary}>
                        %
                      </Typography>
                    </Typography>
                    <Typography variant="label" color={tokens.Text.General.Secondary}>
                      {item.label}
                    </Typography>
                  </div>
                </Tooltip>
              </Container>
            );
          })}
        </div>
      </Container>
    </>
  );
}
